import type { ImageModelsRefreshResponse } from "@nlc/shared-types";
import type { SaveImageModel } from "@nlc/shared-types";

import { Badge } from "@/components/badge";
import { Checkbox } from "@/components/checkbox";
import { Switch } from "@/components/switch";

import { formatImagePrice } from "./imageModelDraft";

export type ImageModelCandidate =
  ImageModelsRefreshResponse["candidates"][number];

const DESCRIPTION_LENGTH = 140;

interface NewModelsSectionProps {
  candidates: ImageModelCandidate[];
  onChange: (model: SaveImageModel) => void;
  onSelect: (candidate: ImageModelCandidate, isSelected: boolean) => void;
  selected: Record<string, SaveImageModel>;
}

export function NewModelsSection({
  candidates,
  onChange,
  onSelect,
  selected,
}: NewModelsSectionProps) {
  return (
    <section className="flex flex-col gap-4 px-xl py-6">
      <h2 className="text-lg font-semibold text-text-primary">New models</h2>
      {candidates.length === 0 && (
        <p className="text-sm text-text-tertiary">
          No new compatible models found.
        </p>
      )}
      {candidates.map((candidate) => {
        const draft = selected[candidate.key];

        return (
          <div
            className="flex flex-col gap-2 rounded-md border border-border-primary p-4"
            key={candidate.key}
          >
            <div className="flex flex-wrap items-center gap-4">
              <Checkbox
                aria-label={`Add ${candidate.displayName}`}
                isSelected={!!draft}
                onChange={(isSelected) => onSelect(candidate, isSelected)}
              >
                {candidate.displayName}
              </Checkbox>
              <span className="text-xs text-text-tertiary">
                {candidate.key}
              </span>
              {draft && (
                <div className="ml-auto flex gap-4">
                  <Switch
                    aria-label={`Alpha generation for ${candidate.displayName}`}
                    isDisabled={!candidate.textToImage}
                    isSelected={draft.alpha.generation}
                    onChange={(generation) =>
                      onChange({
                        ...draft,
                        alpha: { ...draft.alpha, generation },
                      })
                    }
                  >
                    Alpha generation
                  </Switch>
                  <Switch
                    aria-label={`Edit selector for ${candidate.displayName}`}
                    isDisabled={!candidate.edit}
                    isSelected={draft.alpha.edit}
                    onChange={(edit) =>
                      onChange({ ...draft, alpha: { ...draft.alpha, edit } })
                    }
                  >
                    Edit selector
                  </Switch>
                </div>
              )}
            </div>
            <p className="text-sm text-text-secondary">
              {truncate(candidate.description)}
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-text-tertiary">
              {candidate.textToImage && (
                <CandidateEndpoint
                  endpoint={candidate.textToImage}
                  label="Generation"
                />
              )}
              {candidate.edit && (
                <CandidateEndpoint endpoint={candidate.edit} label="Edit" />
              )}
            </div>
          </div>
        );
      })}
    </section>
  );
}

function CandidateEndpoint({
  endpoint,
  label,
}: {
  endpoint: NonNullable<ImageModelCandidate["edit"]>;
  label: string;
}) {
  return (
    <span className="flex items-center gap-2">
      {label}: {endpoint.endpoint} - {formatImagePrice(endpoint.price)}
      {!endpoint.price.verified && (
        <Badge size="small" variant="warning">
          est.
        </Badge>
      )}
    </span>
  );
}

function truncate(text: string): string {
  return text.length > DESCRIPTION_LENGTH
    ? `${text.slice(0, DESCRIPTION_LENGTH).trimEnd()}...`
    : text;
}
